import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNotificationStore } from '@/stores/notifications/notification-store';
import {
  CARIBBEAN_BLUE,
  DANGER_RED,
  WARNING_AMBER,
  INK,
} from '@/styles/tokens';
import type { AgentNotification } from '@/services/api/agentApi';
import { getSafeNotificationNavigationTarget } from './notificationNavigation';

const MAX_DROPDOWN_ITEMS = 10;

const urgencyConfig = {
  critical: { icon: AlertTriangle, color: DANGER_RED.DEFAULT, label: 'Critical' },
  high: { icon: AlertCircle, color: WARNING_AMBER.DEFAULT, label: 'High' },
  normal: { icon: Info, color: CARIBBEAN_BLUE.DEFAULT, label: 'Normal' },
  low: { icon: Info, color: INK[400], label: 'Low' },
} as const;

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function NotificationItem({
  notification,
  onRead,
  onDismiss,
}: {
  notification: AgentNotification;
  onRead: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  const config = urgencyConfig[notification.urgency] || urgencyConfig.normal;
  const Icon = config.icon;
  const isUnread = notification.status === 'pending' || notification.status === 'delivered';
  const navigate = useNavigate();

  const handleClick = () => {
    if (isUnread) onRead(notification.notification_id);
    const navigationTarget = getSafeNotificationNavigationTarget(notification.action_url);
    if (navigationTarget) {
      navigate(navigationTarget);
    }
  };

  return (
    <div
      className={cn(
        'px-3 py-2.5 cursor-pointer transition-colors border-b border-border/50',
        isUnread ? 'bg-accent/30' : 'bg-transparent',
        'hover:bg-accent/50'
      )}
      onClick={handleClick}
    >
      <div className="flex items-start gap-2.5">
        <Icon
          className="h-4 w-4 mt-0.5 flex-shrink-0"
          style={{ color: config.color }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p
              className={cn(
                'text-sm truncate text-foreground',
                isUnread ? 'font-semibold' : 'font-normal'
              )}
            >
              {notification.title}
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); onDismiss(notification.notification_id); }}
              className="p-0.5 hover:bg-accent rounded flex-shrink-0"
              aria-label="Dismiss notification"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          </div>
          {notification.body && (
            <p className="text-xs mt-0.5 line-clamp-2 text-muted-foreground">
              {notification.body}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-muted-foreground">
              {formatTimeAgo(notification.created_at)}
            </span>
            <span className="text-[10px]" style={{ color: config.color }}>
              {notification.source_agent}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotificationCenter({ className }: { className?: string }) {
  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    fetchUnreadCount,
    markRead,
    markDismissed,
    markAllRead,
  } = useNotificationStore();

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Every 60s
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      fetchNotifications(MAX_DROPDOWN_ITEMS);
    }
  };

  const displayNotifications = notifications.slice(0, MAX_DROPDOWN_ITEMS);

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn('relative p-2 hover:bg-accent rounded-md transition-colors', className)}
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 flex items-center justify-center text-[10px] px-1 border-0 bg-destructive text-destructive-foreground"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 max-h-[480px] overflow-hidden p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <DropdownMenuLabel className="p-0 text-sm font-semibold">
            Notifications
          </DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-primary"
              onClick={() => markAllRead()}
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[380px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : displayNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <Bell className="h-8 w-8 mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No notifications
              </p>
            </div>
          ) : (
            displayNotifications.map((n) => (
              <NotificationItem
                key={n.notification_id}
                notification={n}
                onRead={markRead}
                onDismiss={markDismissed}
              />
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
